import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminData } from '../../admin/AdminDataContext';
import noPhoto from '../../assets/shared/no-photo.jpg';
import { ApiError } from '../../lib/api';
import { featureFlags } from '../../lib/featureFlags';
import { formatCellValue, inputPropsFor } from '../../lib/fieldFormat';
import { pickRepresentativePoint } from '../../lib/geometryMetrics';
import { layersApi } from '../../lib/layersApi';
import { mediaUrl } from '../../lib/mediaApi';
import { FeatureAttachmentsSection } from './FeatureAttachmentsSection';
import { HierarchyModal } from './HierarchyModal';
import { InventoryReportModal } from './InventoryReportModal';
import { ObjectPhoto } from './ObjectPhoto';
import { ParentObjectPicker } from './ParentObjectPicker';
import { RelatedRowsSection } from './RelatedRowsSection';
import { geometryTypeLabels, pickObjectTitle, useObjectCard } from './useObjectCard';

/** The full-page object card — opened from the layer table (LayerTable's row
 * action, and this page's own route). The map's "Открыть карточку объекта"
 * opens the different, big-popup ObjectCardModal instead (see that component)
 * — the two intentionally look nothing alike, so they don't share layout, only
 * the useObjectCard data/editing hook. */
export function ObjectCardBody({
  layerId,
  featureId,
  onNavigateFeature,
  onAfterDelete,
  closeLabel,
  onRequestClose,
}: {
  layerId: string;
  featureId: string;
  onNavigateFeature: (featureId: string) => void;
  onAfterDelete: () => void;
  closeLabel: React.ReactNode;
  onRequestClose: () => void;
}) {
  const card = useObjectCard(layerId, featureId, onNavigateFeature);
  const { layer, feature } = card;
  const navigate = useNavigate();
  const { mapSettings } = useAdminData();
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [childPickerOpen, setChildPickerOpen] = useState(false);
  const [addChildError, setAddChildError] = useState<string | null>(null);
  const [addingChild, setAddingChild] = useState(false);
  const [removingChildId, setRemovingChildId] = useState<string | null>(null);
  const [hierarchyModalOpen, setHierarchyModalOpen] = useState(false);
  const [inventoryReportOpen, setInventoryReportOpen] = useState(false);

  const isPointGeometry = feature?.geometry.type === 'Point';

  const handleRemoveChild = async (child: { id: string; layer_id: string }) => {
    setRemovingChildId(child.id);
    setAddChildError(null);
    try {
      await layersApi.setFeatureParent(child.layer_id, child.id, null);
      card.refreshChildren();
    } catch (err) {
      setAddChildError(err instanceof ApiError ? err.message : 'Не удалось отвязать дочерний объект.');
    } finally {
      setRemovingChildId(null);
    }
  };

  const handleAddChild = async (childFeatureId: string, childLayerId: string, snapToParent: boolean) => {
    setAddingChild(true);
    setAddChildError(null);
    try {
      await layersApi.setFeatureParent(childLayerId, childFeatureId, feature!.id, snapToParent);
      card.refreshChildren();
      setChildPickerOpen(false);
    } catch (err) {
      setAddChildError(err instanceof ApiError ? err.message : 'Не удалось привязать дочерний объект.');
    } finally {
      setAddingChild(false);
    }
  };

  const handleCreateChild = async (childLayerId: string) => {
    if (!feature) return;
    const point = pickRepresentativePoint(feature.geometry);
    if (!point) {
      setAddChildError('Не удалось определить координаты для нового объекта.');
      return;
    }
    setAddingChild(true);
    setAddChildError(null);
    try {
      const created = await layersApi.createFeature(childLayerId, { type: 'Point', coordinates: point });
      await layersApi.setFeatureParent(childLayerId, created.id, feature.id, true);
      card.refreshChildren();
      setChildPickerOpen(false);
    } catch (err) {
      setAddChildError(err instanceof ApiError ? err.message : 'Не удалось создать дочерний объект.');
    } finally {
      setAddingChild(false);
    }
  };

  const handleShowOnMap = () => {
    if (!feature || !layer) return;
    navigate('/app', {
      state: {
        focusGeometry: feature.geometry,
        focusProperties: { name: pickObjectTitle(feature.properties), objectType: layer.name, ...feature.properties },
      },
    });
  };

  if (card.loading) return <p className="p-8 text-sm text-text-secondary">Загрузка…</p>;
  if (card.loadError || !layer || !feature) {
    return <p className="p-8 text-sm font-medium text-status-danger">{card.loadError ?? 'Объект не найден'}</p>;
  }

  const geometryLabel = geometryTypeLabels[feature.geometry.type] ?? feature.geometry.type;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-bg-canvas">
      <header className="flex h-[72px] shrink-0 items-center gap-4 border-b border-border-default bg-bg-surface px-8">
        <button
          type="button"
          onClick={() => card.confirmDiscardIfNeeded() && onRequestClose()}
          className="text-sm font-medium text-text-secondary hover:text-text-primary"
        >
          {closeLabel}
        </button>
        <h1 className="truncate text-[17px] font-semibold text-text-primary">{pickObjectTitle(feature.properties)}</h1>

        <div className="ml-auto flex items-center gap-3">
          {card.saveError && <p className="max-w-[260px] text-xs font-medium text-status-danger">{card.saveError}</p>}
          {card.pendingCount > 0 && (
            <>
              <span className="text-[11px] text-text-secondary">Изменено полей: {card.pendingCount}</span>
              <button
                type="button"
                onClick={card.handleDiscard}
                disabled={card.saving}
                className="text-[13px] font-semibold text-text-secondary hover:text-text-primary"
              >
                Отменить
              </button>
              <button
                type="button"
                onClick={card.handleSaveAll}
                disabled={card.saving}
                className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {card.saving ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleShowOnMap}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Показать на карте
          </button>
          {card.canEdit && (
            <button
              type="button"
              onClick={() => card.handleDelete(onAfterDelete)}
              disabled={card.deleting}
              className="flex h-10 items-center rounded-[10px] border border-status-danger px-4 text-[13px] font-medium text-status-danger hover:bg-status-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {card.deleting ? 'Удаляем…' : 'Удалить'}
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[900px] px-8 py-8">
          <div className="grid grid-cols-[220px_1fr] gap-6">
            <section className="flex flex-col items-center gap-4 rounded-2xl border border-border-default bg-bg-surface p-6">
              <ObjectPhoto fileId={feature.photo_file_id} canEdit={card.canEdit} onChange={card.handleSetPhoto} size={160} />
              <div className="w-full">
                <p className="text-[11px] font-medium text-text-secondary">Геометрия</p>
                <p className="mt-1 text-[13px] font-medium text-text-primary">{geometryLabel}</p>
              </div>
              <div className="w-full">
                <p className="text-[11px] font-medium text-text-secondary">Создан</p>
                <p className="mt-1 text-[13px] font-medium text-text-primary">
                  {new Date(feature.created_at).toLocaleString('ru-RU')}
                </p>
                {feature.created_by_name && <p className="text-[11px] text-text-secondary">{feature.created_by_name}</p>}
              </div>
              <div className="w-full">
                <p className="text-[11px] font-medium text-text-secondary">Обновлён</p>
                <p className="mt-1 text-[13px] font-medium text-text-primary">
                  {new Date(feature.updated_at).toLocaleString('ru-RU')}
                </p>
                {feature.updated_by_name && <p className="text-[11px] text-text-secondary">{feature.updated_by_name}</p>}
              </div>
            </section>

            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Атрибуты</h2>
              {!card.canEdit && (
                <p className="mt-1 text-xs text-text-secondary">У вас нет доступа к редактированию этого объекта</p>
              )}

              {layer.fields.length === 0 ? (
                <p className="mt-4 text-xs text-text-secondary">У слоя нет атрибутивных полей</p>
              ) : (
                <div className="mt-4 flex flex-col gap-3">
                  {layer.fields.map((field) => {
                    const isEditing = card.editingCode === field.code;
                    const value = card.effectiveValue(field.code);
                    const isDirty = field.code in card.pending;

                    return (
                      <div key={field.code}>
                        <label className="text-[11px] font-medium text-text-secondary">{field.name}</label>

                        {field.data_type === 'boolean' ? (
                          <div className={`mt-1.5 rounded-[9px] px-3 py-2 ${isDirty ? 'bg-action-primary/10' : ''}`}>
                            <input
                              type="checkbox"
                              checked={Boolean(value)}
                              disabled={!card.canEdit}
                              onChange={(e) => card.stageValue(field, e.target.checked)}
                              className="size-[18px] rounded-[5px] accent-action-primary disabled:opacity-40"
                            />
                          </div>
                        ) : field.data_type === 'dictionary' ? (
                          (() => {
                            const dictId = field.dictionary_id ?? undefined;
                            const dictValues = dictId ? card.dictValuesByDict[dictId] : undefined;
                            const valueId = typeof value === 'string' ? value : null;
                            const matched = dictValues?.find((v) => v.id === valueId);
                            return isEditing ? (
                              <select
                                autoFocus
                                value={valueId ?? ''}
                                onChange={(e) => {
                                  card.stageValue(field, e.target.value || null);
                                  card.setEditingCode(null);
                                }}
                                onBlur={() => card.setEditingCode(null)}
                                className="mt-1.5 h-10 w-full rounded-[9px] border border-action-primary bg-bg-surface px-3 text-[13px] text-text-primary outline-none"
                              >
                                <option value="">—</option>
                                {(dictValues ?? []).map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div
                                onClick={() => card.canEdit && card.setEditingCode(field.code)}
                                className={`mt-1.5 rounded-[9px] px-3 py-2 text-[13px] ${isDirty ? 'bg-action-primary/10' : ''} ${
                                  card.canEdit ? 'cursor-pointer hover:bg-bg-surface-secondary' : ''
                                }`}
                              >
                                {matched ? (
                                  <Link
                                    to={`/app/dictionaries/${dictId}/values/${matched.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-medium text-action-primary hover:underline"
                                  >
                                    {matched.name}
                                  </Link>
                                ) : (
                                  <span className="text-text-primary">—</span>
                                )}
                              </div>
                            );
                          })()
                        ) : isEditing ? (
                          <>
                            <input
                              autoFocus
                              {...inputPropsFor(field.data_type)}
                              value={card.editDraft}
                              onChange={(e) => {
                                card.setEditDraft(e.target.value);
                                card.setEditError(null);
                              }}
                              onBlur={() => card.commitTextEdit(field)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') card.commitTextEdit(field);
                                if (e.key === 'Escape') card.setEditingCode(null);
                              }}
                              className={`mt-1.5 h-10 w-full rounded-[9px] border bg-bg-surface px-3 text-[13px] text-text-primary outline-none ${
                                card.editError ? 'border-status-danger' : 'border-action-primary'
                              }`}
                            />
                            {card.editError && <p className="mt-1 text-[11px] font-medium text-status-danger">{card.editError}</p>}
                          </>
                        ) : (
                          <p
                            onClick={() => card.startEdit(field)}
                            className={`mt-1.5 rounded-[9px] px-3 py-2 text-[13px] text-text-primary ${
                              isDirty ? 'bg-action-primary/10' : ''
                            } ${card.canEdit ? 'cursor-pointer hover:bg-bg-surface-secondary' : ''}`}
                          >
                            {formatCellValue(value)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {featureFlags.objectHierarchy && (
            <section className="mt-6 rounded-2xl border border-border-default bg-bg-surface p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-text-primary">Иерархия</h2>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setInventoryReportOpen(true)}
                    className="text-[12px] font-medium text-action-primary hover:underline"
                  >
                    Отчёт по инвентаризации
                  </button>
                  <button
                    type="button"
                    onClick={() => setHierarchyModalOpen(true)}
                    className="text-[12px] font-medium text-action-primary hover:underline"
                  >
                    Показать иерархию
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-[11px] font-medium text-text-secondary">Родительский объект</p>
                {feature.parent ? (
                  <div className="mt-1.5 flex items-center gap-3">
                    <Link
                      to={`/app/data/layers/${feature.parent.layer_id}/objects/${feature.parent.id}`}
                      className="text-[13px] font-medium text-action-primary hover:underline"
                    >
                      {feature.parent.title}
                    </Link>
                    <span className="text-[11px] text-text-secondary">{feature.parent.layer_name}</span>
                    {card.canEdit && (
                      <button
                        type="button"
                        onClick={() => card.setParent(null)}
                        disabled={card.parentSaving}
                        className="text-[11px] font-medium text-text-secondary hover:text-status-danger disabled:opacity-50"
                      >
                        Убрать
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="mt-1.5 text-[13px] text-text-secondary">Не задан</p>
                )}
                {card.canEdit && !parentPickerOpen && (
                  <button
                    type="button"
                    onClick={() => setParentPickerOpen(true)}
                    className="mt-2 text-[12px] font-medium text-action-primary hover:underline"
                  >
                    {feature.parent ? 'Изменить' : 'Задать родительский объект'}
                  </button>
                )}
                {card.parentError && <p className="mt-1 text-[11px] font-medium text-status-danger">{card.parentError}</p>}
                {parentPickerOpen && mapSettings?.id && (
                  <ParentObjectPicker
                    mapId={mapSettings.id}
                    excludeFeatureId={feature.id}
                    snapCheckboxLabel={isPointGeometry ? 'Переместить этот объект к координатам родителя' : undefined}
                    snapCheckboxEnabledForLayer={() => isPointGeometry}
                    onCancel={() => setParentPickerOpen(false)}
                    onPick={(parentId, _parentLayerId, snapToParent) => {
                      card.setParent(parentId, snapToParent);
                      setParentPickerOpen(false);
                    }}
                  />
                )}
              </div>

              <div className="mt-5">
                <p className="text-[11px] font-medium text-text-secondary">
                  Дочерние объекты{card.children.length > 0 ? ` (${card.children.length})` : ''}
                </p>
                {card.children.length === 0 ? (
                  <p className="mt-1.5 text-[13px] text-text-secondary">Нет</p>
                ) : (
                  <div className="mt-1.5 flex flex-col gap-1.5">
                    {card.children.map((child) => (
                      <div key={child.id} className="flex items-center gap-3">
                        <Link
                          to={`/app/data/layers/${child.layer_id}/objects/${child.id}`}
                          className="text-[13px] font-medium text-action-primary hover:underline"
                        >
                          {child.title}
                        </Link>
                        <span className="text-[11px] text-text-secondary">{child.layer_name}</span>
                        {card.canEdit && (
                          <button
                            type="button"
                            onClick={() => handleRemoveChild(child)}
                            disabled={removingChildId === child.id}
                            className="text-[11px] font-medium text-text-secondary hover:text-status-danger disabled:opacity-50"
                          >
                            {removingChildId === child.id ? 'Отвязываем…' : 'Убрать'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {card.canEdit && !childPickerOpen && (
                  <button
                    type="button"
                    onClick={() => setChildPickerOpen(true)}
                    className="mt-2 text-[12px] font-medium text-action-primary hover:underline"
                  >
                    Добавить дочерний объект
                  </button>
                )}
                {addChildError && <p className="mt-1 text-[11px] font-medium text-status-danger">{addChildError}</p>}
                {childPickerOpen && mapSettings?.id && (
                  <ParentObjectPicker
                    mapId={mapSettings.id}
                    excludeFeatureId={feature.id}
                    snapCheckboxLabel="Переместить дочерний объект к координатам этого"
                    snapCheckboxEnabledForLayer={(l) => l?.geometry_type === 'point'}
                    onCancel={() => setChildPickerOpen(false)}
                    onPick={(childId, childLayerId, snapToParent) => {
                      if (!addingChild) handleAddChild(childId, childLayerId, snapToParent);
                    }}
                    onCreateNew={(childLayerId) => {
                      if (!addingChild) handleCreateChild(childLayerId);
                    }}
                  />
                )}
              </div>
            </section>
          )}

          <section className="mt-6 rounded-2xl border border-border-default bg-bg-surface p-6 empty:hidden">
            <RelatedRowsSection layerId={layerId} featureId={feature.id} featureTitle={pickObjectTitle(feature.properties)} />
          </section>

          <section className="mt-6 rounded-2xl border border-border-default bg-bg-surface p-6 empty:hidden">
            <FeatureAttachmentsSection
              layerId={layerId}
              featureId={feature.id}
              properties={feature.properties}
              canEdit={card.canEdit}
              onChanged={card.setFeatureProperties}
            />
          </section>
        </div>
      </div>

      <div className="shrink-0 border-t border-border-default bg-bg-surface px-8 py-3">
        <div className="mx-auto flex w-full max-w-[900px] items-center gap-3">
          <input
            type="text"
            value={card.stripSearch}
            onChange={(e) => card.setStripSearch(e.target.value)}
            placeholder="Поиск по объектам слоя…"
            className="h-9 w-64 shrink-0 rounded-[9px] bg-bg-surface-secondary px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
          />
          <span className="shrink-0 text-[11px] text-text-secondary">
            {card.filteredSiblings.length.toLocaleString('ru-RU')} из {card.siblingFeatures.length.toLocaleString('ru-RU')}
          </span>
          <div className="h-scroll-ribbon flex-1 overflow-x-auto overflow-y-hidden py-1.5">
            <div className="flex items-center gap-2 px-0.5">
              {card.filteredSiblings.map((f) => {
                const active = f.id === feature.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => card.goToFeature(f.id)}
                    title={pickObjectTitle(f.properties)}
                    className={`flex w-24 shrink-0 flex-col items-center gap-1 rounded-lg p-1.5 text-left transition-colors ${
                      active ? 'bg-action-primary/10 ring-2 ring-inset ring-action-primary' : 'hover:bg-bg-surface-secondary'
                    }`}
                  >
                    <img
                      src={f.photo_file_id ? mediaUrl(f.photo_file_id) : noPhoto}
                      alt=""
                      className="size-14 rounded-lg object-cover"
                    />
                    <span
                      className={`w-full truncate text-center text-[10px] font-medium ${
                        active ? 'text-action-primary' : 'text-text-secondary'
                      }`}
                    >
                      {pickObjectTitle(f.properties)}
                    </span>
                  </button>
                );
              })}
              {card.filteredSiblings.length === 0 && <p className="py-2 text-xs text-text-secondary">Ничего не найдено</p>}
            </div>
          </div>
        </div>
      </div>

      {hierarchyModalOpen && (
        <HierarchyModal layerId={layerId} featureId={feature.id} onClose={() => setHierarchyModalOpen(false)} />
      )}
      {inventoryReportOpen && (
        <InventoryReportModal layerId={layerId} featureId={feature.id} onClose={() => setInventoryReportOpen(false)} />
      )}
    </div>
  );
}
